import { createFileRoute, Link, notFound, redirect, useLoaderData, useNavigate, useRouter, useSearch } from "@tanstack/react-router";
import { isImportedEventId, importedEventSlug } from "@/lib/item-link";

import type { SearchSchemaInput } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { Heart, MapPin, Search, Pencil, ArrowLeft, CalendarClock, Info as InfoIcon, ListChecks, Ticket, Clock, MessageSquare, UtensilsCrossed } from "lucide-react";
import { StarIcon } from "@/components/icons/StarIcon";

import { type Item } from "@/data/places";
import { usePublicPlaces } from "@/hooks/use-public-places";
import { supabase } from "@/integrations/supabase/client";
import { useFavorites } from "@/hooks/useFavorites";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAutoTranslate, useProperName } from "@/i18n/useAutoTranslate";
import { useCity } from "@/context/CityContext";
import heroBanner from "@/assets/hero-helsinki.webp";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CountdownTicker } from "@/components/EventCountdown";
import { SmartImage } from "@/components/ui/SmartImage";
import { PlaceGallery } from "@/components/PlaceGallery";
import { PlaceReviews } from "@/components/PlaceReviews";
import { useReviewStat } from "@/hooks/use-review-stats";
import { useEntityTranslations } from "@/hooks/use-entity-translations";
import { BookingDialog } from "@/components/BookingDialog";
import { partnerHref } from "@/lib/partner-link";
import { buildHreflangLinks, buildLocaleMeta } from "@/lib/i18n-paths";
import { fetchEntityTranslations, langFromPathname, tr } from "@/lib/i18n-content";
import { usePublicSettings } from "@/hooks/use-public-settings";
import { MiniMapPreview } from "@/components/MiniMapPreview";
import PlaceLocationsMap, { type PlaceLocationPin } from "@/components/PlaceLocationsMap";
import { ClientOnly } from "@tanstack/react-router";
import { MobileMapSheet } from "@/components/place/MobileMapSheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { PlaceMenuSection } from "@/components/place/PlaceMenuSection";
import { DistrictLinkBanner } from "@/components/districts/DistrictLinkBanner";
import { HoursStatus } from "@/components/menus/HoursStatus";
import { menuHref, type PlaceMenuDoc } from "@/lib/place-menus";
import { hasPlaceMenu } from "@/lib/place-menu";

const TABS = ["overview", "details", "tickets", "schedule", "reviews"] as const;
type Tab = (typeof TABS)[number];

function MissingFieldWarning({ field }: { field: string }) {
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs sm:text-sm">
      <span className="font-semibold">Missing schema text:</span>{" "}
      <span>“{field}” is empty. This tab will look bare or be hidden on the public page. Add content in the admin editor.</span>
    </div>
  );
}


// Safe translation: never throw, always return a usable string.
function safeT(t: (k: string) => string, key: string, fallback: string): string {
  try {
    const v = t(key);
    if (!v || v === key) return fallback;
    return v;
  } catch {
    return fallback;
  }
}

function useSafeT(): (k: string) => string {
  try {
    const t = useLanguage().t as unknown as (k: string) => string;
    return t;
  } catch {
    return (k: string) => k;
  }
}

export function PlaceNotFound() {
  const t = useSafeT();
  return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <h1 className="font-display text-3xl sm:text-5xl">{safeT(t, "place.notfound.title", "Place not found")}</h1>
      <p className="text-muted-foreground mt-3">{safeT(t, "place.notfound.body", "We couldn't find what you were looking for.")}</p>
      <Link to="/eat" className="inline-flex mt-6 bg-navy text-white px-6 py-3 rounded-full text-sm">{safeT(t, "place.notfound.back", "Browse places")}</Link>
    </div>
  );
}

export function PlaceError({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  const t = useSafeT();
  if (typeof console !== "undefined") console.error("[place]", error);
  return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <h1 className="font-display text-2xl sm:text-3xl">{safeT(t, "place.error.title", "Something went wrong")}</h1>
      <p className="text-muted-foreground mt-2 text-sm">
        We hit a snag loading this place. Try again or go back.
      </p>
      <div className="mt-6 flex justify-center gap-2">
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-navy text-white px-6 py-3 rounded-full text-sm">{safeT(t, "common.tryagain", "Try again")}</button>
        <Link to="/" className="border border-border px-6 py-3 rounded-full text-sm">{safeT(t, "common.gohome", "Go home")}</Link>
      </div>
    </div>
  );
}

export function PlaceRoute() {
  return (
    <ErrorBoundary fallback={(error, reset) => <PlaceError error={error} reset={reset} />}>
      <PlaceDetail />
    </ErrorBoundary>
  );
}

function PlaceDetail() {
  const { item, related, reviewStat: initialReviewStat, locations: rawLocations } = useLoaderData({ strict: false }) as any;
  // Always include the master row's own address/coords as a pin, alongside any
  // extra rows configured in place_locations. Dedupe against branches that
  // sit at effectively the same coordinates (~11m tolerance at 4 decimals).
  const locations = (() => {
    // Keep every branch — even ones we couldn't geocode — so the list always
    // reflects reality. The map component itself only draws pins for entries
    // that have coordinates.
    const rows = ((rawLocations ?? []) as any[]) as Array<PlaceLocationPin & { phone?: string | null; email?: string | null; opening_hours?: any; postal_code?: string | null; region?: string | null; is_primary?: boolean }>;
    const hasMasterCoords = item.lat != null && item.lng != null;
    const masterKey = hasMasterCoords ? `${Number(item.lat).toFixed(4)},${Number(item.lng).toFixed(4)}` : null;
    const dupOfMaster = masterKey
      ? rows.some((r) => r.lat != null && r.lng != null && `${Number(r.lat).toFixed(4)},${Number(r.lng).toFixed(4)}` === masterKey)
      : false;
    const hasPrimaryRow = rows.some((r) => (r as any).is_primary);
    const masterPin = hasMasterCoords && !dupOfMaster && !hasPrimaryRow
      ? [{
          id: "primary",
          label: item.name,
          address: item.address ?? null,
          city_slug: item.city,
          lat: Number(item.lat),
          lng: Number(item.lng),
          phone: (item as any).phone ?? null,
          email: (item as any).email ?? null,
          opening_hours: (item as any).openingHours ?? null,
          postal_code: null,
          region: (item as any).region ?? null,
          is_primary: true,
        }]
      : [];
    return [...masterPin, ...rows];
  })();
  const mapLocations = (locations as any[]).filter((l) => l.lat != null && l.lng != null);
  const { has, toggle } = useFavorites();
  const { t, lang } = useLanguage();
  const saved = has(item.id);
  const liveReviewStat = useReviewStat(item.id);
  const reviewStat = liveReviewStat.count > 0 ? liveReviewStat : initialReviewStat;
  const hasReviews = reviewStat.count >= 5;
  const rating = reviewStat.avg;
  const reviews = reviewStat.count;
  const [tab, setTab] = useState<Tab>("overview");
  const { preview: previewMode, loc: locFromUrl } = useSearch({ strict: false }) as { preview?: number; loc?: string };
  const isPreview = previewMode === 1;
  const navigate = useNavigate();
  const placeKey = (item as any)?.slug ?? (item as any)?.id ?? "";
  const storageKey = placeKey ? `place:selectedLoc:${placeKey}` : "";

  const [mapOpen, setMapOpen] = useState<boolean>(false);
  const isMobile = useIsMobile();

  // Which pin's details are shown below the map. Prefer explicit selection from
  // URL (?loc=) or localStorage so refreshing the page keeps the same pick.
  // Otherwise default to the primary/first location, then upgrade to the
  // location closest to the user's position once geolocation resolves.
  const initialSelectedId = (() => {
    const ids = new Set((locations as any[]).map((l) => l.id));
    if (locFromUrl && ids.has(locFromUrl)) return locFromUrl;
    if (typeof window !== "undefined" && storageKey) {
      const stored = window.localStorage.getItem(storageKey);
      if (stored && ids.has(stored)) return stored;
    }
    return (locations[0] as any)?.id ?? null;
  })();
  const [selectedLocId, setSelectedLocIdState] = useState<string | null>(initialSelectedId);
  const hasExplicitSelection = Boolean(
    (locFromUrl && (locations as any[]).some((l) => l.id === locFromUrl)) ||
    (typeof window !== "undefined" && storageKey &&
      (() => {
        const s = window.localStorage.getItem(storageKey);
        return s && (locations as any[]).some((l) => l.id === s);
      })()),
  );
  const setSelectedLocId = (id: string | null | ((prev: string | null) => string | null)) => {
    setSelectedLocIdState((prev) => {
      const next = typeof id === "function" ? (id as any)(prev) : id;
      if (typeof window !== "undefined" && storageKey) {
        if (next) window.localStorage.setItem(storageKey, next);
        else window.localStorage.removeItem(storageKey);
      }
      (navigate as any)({
        search: (s: any) => ({ ...s, loc: next ?? undefined }),
        replace: true,
        resetScroll: false,
      }).catch(() => {});

      return next;
    });
  };

  useEffect(() => {
    if (typeof window === "undefined" || !("geolocation" in navigator)) return;
    if (locations.length < 2) return;
    if (hasExplicitSelection) return;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        const { latitude, longitude } = pos.coords;
        let bestId: string | null = null;
        let bestDist = Infinity;
        for (const l of locations as any[]) {
          if (l.lat == null || l.lng == null) continue;
          const d = haversineKm(latitude, longitude, Number(l.lat), Number(l.lng));
          if (d < bestDist) { bestDist = d; bestId = l.id; }
        }
        if (bestId) setSelectedLocId(bestId);
      },
      () => { /* denied / unavailable — keep default */ },
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 5 * 60 * 1000 },
    );
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations.map((l: any) => l.id).join("|")]);

  // Keep the selected pin in sync with the URL so browser back/forward and any
  // other ?loc= change (e.g. shared link updates) reflect in the details panel.
  useEffect(() => {
    const ids = new Set((locations as any[]).map((l) => l.id));
    const next = locFromUrl && ids.has(locFromUrl) ? locFromUrl : null;
    setSelectedLocIdState((prev) => {
      if (next && next !== prev) {
        if (typeof window !== "undefined" && storageKey) {
          window.localStorage.setItem(storageKey, next);
        }
        return next;
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locFromUrl, locations.map((l: any) => l.id).join("|")]);

  const [openNowOnly, setOpenNowOnly] = useState(false);
  const openLocations = (locations as any[]).filter((l) => isOpenNow(l.opening_hours));
  const visibleLocations = openNowOnly && openLocations.length > 0 ? openLocations : locations;
  // If the currently selected pin isn't in the filtered set, fall back to the
  // first visible one so the details panel stays in sync.
  useEffect(() => {
    if (!openNowOnly) return;
    if (!visibleLocations.some((l: any) => l.id === selectedLocId)) {
      setSelectedLocId((visibleLocations[0] as any)?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openNowOnly, visibleLocations.map((l: any) => l.id).join("|")]);


  const router = useRouter();
  const { setCity } = useCity();

  // Keep this page in sync with admin/partner edits without a full reload.
  useEffect(() => {
    const slug = String(item?.id ?? "");
    const uuid = String((item as any)?.dbId ?? "");
    if (!slug) return;
    const ch = supabase
      .channel(`place-page:${slug}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "places", filter: `slug=eq.${slug}` },
        () => {
          void router.invalidate();
        },
      );
    if (uuid) {
      ch.on(
        "postgres_changes",
        { event: "*", schema: "public", table: "place_locations", filter: `place_id=eq.${uuid}` },
        () => {
          void router.invalidate();
        },
      );
    }
    ch.subscribe();
    return () => {
      void supabase.removeChannel(ch);
    };
  }, [item?.id, (item as any)?.dbId, router]);

  // Auto-translate user-facing content fields into the active language.
  // Prefer the exact Finnish copy saved in Admin. Runtime translation is only
  // a fallback for fields that have not been manually translated yet.
  const entityT = useEntityTranslations("place", (item as any).dbId);
  const tName = useProperName(item.name);
  const autoDescription = useAutoTranslate(item.description);
  const autoOverviewBody = useAutoTranslate(item.overviewBody);
  const autoDetailsBody = useAutoTranslate(item.detailsBody);
  const autoTicketsInfo = useAutoTranslate(item.ticketsInfo);
  const autoScheduleInfo = useAutoTranslate(item.scheduleInfo);
  const autoArea = useAutoTranslate(item.area);
  const tDescription = entityT("description", autoDescription);
  const tOverviewBody = entityT("overview_body", autoOverviewBody);
  const tDetailsBody = entityT("details_body", autoDetailsBody);
  const tTicketsInfo = entityT("tickets_info", autoTicketsInfo);
  const tScheduleInfo = entityT("schedule_info", autoScheduleInfo);
  const tArea = entityT("area", autoArea);
  const tCategory = useAutoTranslate(item.category);
  const tPrice = useAutoTranslate(item.price);
  const backLabel = useAutoTranslate("Back");
  const noReviewsLabel = useAutoTranslate("no reviews yet");
  const starsLabel = useAutoTranslate("stars");
  const buyTicketsLabel = useAutoTranslate("buy tickets");
  const ticketsPricingLabel = useAutoTranslate("tickets & pricing");
  const priceLabel = useAutoTranslate("price");
  const seeWebsiteLabel = useAutoTranslate("see website");
  const bookReserveLabel = useAutoTranslate("book / reserve");
  const noBookingLinkLabel = useAutoTranslate("no booking link available yet.");
  const scheduleLabel = useAutoTranslate("schedule");
  const noScheduleInfoLabel = useAutoTranslate("no schedule info yet — check the website for the latest times.");
  const reviewsHeadingLabel = useAutoTranslate("reviews");

  const galleryImages = Array.from(
    new Set([item.image, ...((item.gallery ?? []) as string[])].filter(Boolean))
  );
  const hasMultiPhoto = galleryImages.length > 1;
  const sideTop = galleryImages[1];
  const sideBottom = galleryImages[2];
  const hasSidePhotos = Boolean(sideTop || sideBottom);

  const settings = usePublicSettings();
  const taglineKey =
    item.section === "eat" ? "place.tagline.eat" :
    item.section === "do" ? "place.tagline.do" : "place.tagline.happening";
  const promoKey =
    item.section === "eat" ? "place.promo.eat" :
    item.section === "do" ? "place.promo.do" : "place.promo.happening";
  const searchKey =
    item.section === "eat" ? "place.search.eat" :
    item.section === "do" ? "place.search.do" : "place.search.happening";
  const taglineOverride =
    item.section === "eat" ? settings.place_tagline_eat :
    item.section === "do" ? settings.place_tagline_do : settings.place_tagline_happening;
  const promoTitleOverride =
    item.section === "eat" ? settings.place_promo_title_eat :
    item.section === "do" ? settings.place_promo_title_do : settings.place_promo_title_happening;
  const searchOverride =
    item.section === "eat" ? settings.place_search_eat :
    item.section === "do" ? settings.place_search_do : settings.place_search_happening;
  const tTaglineOverride = useAutoTranslate(taglineOverride || "");
  const tPromoTitleOverride = useAutoTranslate(promoTitleOverride || "");
  const tPromoBodyOverride = useAutoTranslate(settings.place_promo_body || "");
  const tSearchOverride = useAutoTranslate(searchOverride || "");
  const tCtaTitleOverride = useAutoTranslate(settings.place_cta_title || "");
  const tCtaBodyOverride = useAutoTranslate(settings.place_cta_body || "");

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-4 sm:pt-8 pb-28 sm:pb-16">
        <button
          type="button"
          onClick={() => {
            const fallback =
              item.section === "eat" ? "/eat" :
              item.section === "do" ? "/do" :
              item.section === "happening" ? "/happening" : "/";
            const cameFromApp =
              typeof document !== "undefined" &&
              document.referrer &&
              document.referrer.startsWith(window.location.origin);
            if (cameFromApp) {
              router.history.back();
            } else {
              // Sync city context so the listing matches this place's city
              if (item.city) setCity(item.city);
              router.navigate({ to: fallback });
            }
          }}
          className="mb-4 sm:mb-5 inline-flex items-center gap-2 rounded-full border border-navy/10 bg-white text-navy px-3 py-1.5 text-xs sm:text-sm font-medium hover:border-navy/25 hover:bg-slate-50 transition-colors"
          aria-label={backLabel}
        >
          <ArrowLeft className="size-4" />
          {backLabel}
        </button>

        <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-6">
          <div className="min-w-0">
            <h1 className="font-heading text-navy text-[28px] leading-[1.05] sm:text-4xl md:text-5xl tracking-tight break-words">
              {tName}
            </h1>
            <DistrictLinkBanner placeSlug={item.id} />
            {item.section === "eat" || (item as any).hoursSource || (item as any).openingHours ? (
              <div className="mt-3 flex flex-wrap items-center gap-2.5">
                <HoursStatus
                  source={
                    (item as any).hoursSource ??
                    ((item as any).openingHours || (item as any).scheduleInfo
                      ? {
                          opening_hours: (item as any).openingHours,
                          schedule_info: (item as any).scheduleInfo ?? null,
                        }
                      : null)
                  }
                />
                {(hasPlaceMenu((item as any).menu) || ((item as any).menuDocs ?? []).some((d: PlaceMenuDoc) => menuHref(d))) ? (
                  <a
                    href="#menu"
                    className="inline-flex items-center gap-1.5 rounded-full bg-navy px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-royal"
                  >
                    <UtensilsCrossed className="size-3.5" />
                    View menu
                  </a>
                ) : null}
              </div>
            ) : null}
            {hasReviews ? (
              <div className="mt-2.5 sm:mt-4 flex items-center gap-2 sm:gap-3 flex-wrap">
                <div className="flex gap-0.5" aria-label={`${rating.toFixed(1)} ${starsLabel}`}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <StarIcon
                      key={i}
                      className={`size-4 sm:size-6 ${i < Math.round(rating) ? "text-[var(--star)]" : "text-[var(--star)]/25"}`}
                    />
                  ))}
                </div>
                <span className="font-heading text-navy text-sm sm:text-lg">
                  {rating.toFixed(1)} · ({reviews} {t("place.reviews")})
                </span>
              </div>
            ) : null}
          </div>


          <div className="flex items-center gap-1.5 sm:gap-3 self-stretch md:self-start w-full md:w-auto">
            {item.section === "happening" && (item.buyTicketUrl || item.reserveLink || item.link) && (
              <a
                href={partnerHref(item.id, "tickets")}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 flex-1 md:flex-none justify-center bg-navy hover:bg-royal active:scale-[0.98] transition-all text-white px-2.5 sm:px-6 h-10 sm:h-11 rounded-full text-[11px] sm:text-sm font-semibold inline-flex items-center gap-1 sm:gap-2 shadow-md shadow-navy/20"
              >
                <CalendarClock className="size-3.5 sm:size-4 shrink-0" />
                <span className="whitespace-nowrap">{buyTicketsLabel}</span>
              </a>
            )}
          </div>


        </header>

{item.section === "happening" && (item.startsAt || item.endsAt || item.eventStatusOverride) && item.showCountdown !== false && (
          <div className="mt-5 sm:mt-6">
            <CountdownTicker
              startsAt={item.startsAt}
              endsAt={item.endsAt}
              statusOverride={item.eventStatusOverride ?? null}
              show={Boolean(item.showCountdown)}
            />
          </div>
        )}


        <section className={`relative mt-3 sm:mt-5 grid grid-cols-1 gap-2 sm:gap-3 ${hasSidePhotos ? "md:grid-cols-[1fr_240px]" : ""}`}>
          <button
            onClick={() => toggle(item.id)}
            aria-pressed={saved}
            aria-label={saved ? t("place.saved") : t("place.save")}
            className={`absolute top-3 right-3 z-10 inline-flex items-center justify-center rounded-full transition size-10 sm:size-11 shadow-md ${
              saved
                ? "bg-teal text-white"
                : "bg-white/90 text-navy hover:bg-white"
            }`}
          >
            <Heart className="size-[18px]" fill={saved ? "currentColor" : "none"} strokeWidth={2.2} />
          </button>
          {hasMultiPhoto ? (
            <PlaceGallery images={galleryImages} alt={item.name} />
          ) : (
            <div className="relative rounded-2xl overflow-hidden bg-slate-100 aspect-[4/3] md:aspect-auto md:h-[420px]">
              <SmartImage src={item.image} alt={item.category ? `${item.category} — ${item.name}` : `Photo of ${item.name}`} className="size-full object-cover" />
            </div>
          )}

          {hasSidePhotos && (
            <div className="grid grid-cols-2 md:grid-cols-1 gap-2 sm:gap-3 md:h-[420px]">
              {sideTop && (
                <div className="relative rounded-2xl overflow-hidden bg-slate-100 aspect-square md:aspect-auto">
                  <SmartImage src={sideTop} alt="" className="size-full object-cover" />
                </div>
              )}
              {sideBottom && (
                <div className="relative rounded-2xl overflow-hidden bg-slate-100 aspect-square md:aspect-auto">
                  <SmartImage src={sideBottom} alt="" className="size-full object-cover" />
                </div>
              )}
            </div>
          )}
        </section>


        {isPreview && (
          <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-3 sm:px-4 py-2.5 text-xs sm:text-sm flex items-center gap-2">
            <span className="inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5">PREVIEW</span>
            <span>Admin preview — showing all tabs regardless of content. Warnings below flag missing schema text before you publish.</span>
          </div>
        )}

        <nav className="mt-6 sm:mt-8 flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3 sm:gap-4 pb-1">
          <div className="w-full sm:w-auto">
            <ul className="flex w-full sm:w-auto items-center gap-0.5 sm:gap-2 p-1 sm:px-1.5 sm:py-1.5 rounded-full bg-white/90 backdrop-blur border border-navy/10 shadow-[0_4px_16px_-10px_rgba(15,17,42,0.25)] sm:shadow-[0_6px_20px_-12px_rgba(15,17,42,0.25)]">
              {TABS.filter((tk) => {
                if (isPreview) return true;
                if (item.section !== "eat") return true;
                if (tk === "tickets") return false;
                if (tk === "schedule") return Boolean(item.scheduleInfo) || Boolean((item as any).openingHours);
                return true;
              }).map((tk) => {
                const active = tab === tk;
                const Icon = tk === "overview" ? InfoIcon
                  : tk === "details" ? ListChecks
                  : tk === "tickets" ? Ticket
                  : tk === "schedule" ? Clock
                  : MessageSquare;
                return (
                  <li key={tk} className="flex-1 sm:flex-none min-w-0">
                    <button
                      onClick={() => setTab(tk)}
                      aria-pressed={active}
                      aria-label={t(`place.tab.${tk}` as const)}
                      style={{ fontFamily: '"Poppins", system-ui, sans-serif' }}
                      className={`w-full inline-flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-4 py-1.5 sm:py-2 rounded-full text-[10px] sm:text-sm font-semibold whitespace-nowrap transition-all ${
                        active
                          ? "bg-navy text-white shadow-[0_6px_16px_-6px_rgba(15,17,42,0.55)]"
                          : "text-navy/60 hover:text-navy hover:bg-navy/5"
                      }`}
                    >
                      <Icon className={`size-3 sm:size-4 shrink-0 ${active ? "opacity-100" : "opacity-70"}`} />
                      <span className="hidden sm:inline sm:truncate">{t(`place.tab.${tk}` as const)}</span>
                      <span className="sm:hidden truncate">{t(`place.tabShort.${tk}` as const)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
          {(item.address || locations.length > 0) && (
            <button
              type="button"
              onClick={() => setMapOpen((v) => !v)}
              aria-expanded={mapOpen}
              style={{ fontFamily: '"Poppins", system-ui, sans-serif' }}
              className="inline-flex items-center gap-2 text-navy text-sm sm:text-lg font-semibold hover:opacity-80 shrink-0 px-4 sm:px-0"
            >
              <MapPin className="size-4 sm:size-5 text-teal" fill="var(--teal)" stroke="white" />
              <span className="underline underline-offset-4">
                {mapOpen ? t("place.hideMap") : t("place.showMap")}
              </span>
              {locations.length > 1 && (
                <span className="ml-1 px-2 py-0.5 rounded-full bg-teal/15 text-teal text-[11px] font-semibold tabular-nums">
                  {locations.length}
                </span>
              )}
            </button>
          )}
        </nav>

        {/* Compact always-visible location preview (saved coordinates + address). */}
        {!mapOpen && mapLocations.length > 0 && (() => {
          const pin = (mapLocations as any[]).find((l) => l.id === selectedLocId) ?? (mapLocations as any[])[0];
          return (
            <section aria-label="Location preview" className="mt-4">
              <ClientOnly fallback={<div className="h-[150px] w-full rounded-xl border border-navy/10 bg-navy/5" />}>
                <MiniMapPreview
                  lat={Number(pin.lat)}
                  lng={Number(pin.lng)}
                  address={pin.address ?? item.address ?? null}
                  city={pin.city_slug ?? item.city ?? null}
                  postalCode={pin.postal_code ?? null}
                  name={item.name}
                  height={150}
                  className="cursor-pointer"
                />
              </ClientOnly>
              <button
                type="button"
                onClick={() => setMapOpen(true)}
                className="mt-2 text-[12px] font-semibold text-royal underline underline-offset-2 hover:opacity-80"
              >
                {mapLocations.length > 1 ? `View all ${mapLocations.length} locations` : "Open full map"}
              </button>
            </section>
          );
        })()}




{(() => {
          if (locations.length === 0) return null;

          const mapBody = (
            <>
              {openLocations.length > 0 && (
                <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setOpenNowOnly((v) => !v)}
                    role="switch"
                    aria-checked={openNowOnly}
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${openNowOnly ? "bg-teal text-white border-teal shadow-sm" : "bg-white text-navy border-navy/20 hover:border-navy/40"}`}
                  >
                    <span className={`inline-block size-2 rounded-full ${openNowOnly ? "bg-white" : "bg-emerald-500"}`} aria-hidden />
                    Open now
                    <span className={`ml-1 tabular-nums ${openNowOnly ? "text-white/90" : "text-navy/60"}`}>
                      {openLocations.length}/{locations.length}
                    </span>
                  </button>
                  {openNowOnly && openLocations.length < locations.length && (
                    <span className="text-xs text-navy/60">Showing open locations only</span>
                  )}
                </div>
              )}
              <ClientOnly fallback={<div className="w-full h-[300px] rounded-2xl border border-navy/15 bg-navy/5" />}>
                <PlaceLocationsMap
                  locations={(visibleLocations as any[]).filter((l) => l.lat != null && l.lng != null) as any}
                  height={isMobile ? 260 : 320}
                  selectedId={selectedLocId}
                  onSelect={(id) => setSelectedLocId(id)}
                />
              </ClientOnly>
              <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {visibleLocations.map((loc: any, idx: number) => {
                  const active = loc.id === selectedLocId;
                  const hasPin = loc.lat != null && loc.lng != null;
                  return (
                    <li
                      key={loc.id ?? idx}
                      className={`rounded-2xl border px-3 py-2.5 text-sm cursor-pointer transition ${active ? "border-navy bg-navy/[0.04] shadow-sm" : "border-navy/10 bg-white hover:border-navy/20"}`}
                      onClick={() => setSelectedLocId(loc.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedLocId(loc.id); } }}
                      aria-pressed={active}
                    >
                      <div className="flex items-center gap-2 font-semibold text-navy">
                        <span className={`inline-flex items-center justify-center size-5 rounded-full text-[10px] font-bold ${active ? "bg-teal text-white" : "bg-navy/10 text-navy/70"}`}>{idx + 1}</span>
                        <span className="truncate">{loc.label || loc.city_slug || `Location ${idx + 1}`}</span>
                        {loc.is_primary && <span className="ml-1 px-1.5 py-0.5 rounded bg-teal/15 text-teal text-[10px] uppercase tracking-widest">Main</span>}
                        {!hasPin && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] uppercase tracking-widest">No pin</span>}
                        {active && <span className="ml-auto text-[10px] uppercase tracking-widest text-teal font-bold">Selected</span>}
                      </div>
                      {loc.address && (
                        <div className="mt-1 text-navy/75">
                          <a
                            href={`https://maps.google.com/?q=${encodeURIComponent([loc.address, loc.city_slug, loc.postal_code].filter(Boolean).join(", "))}`}
                            target="_blank"
                            rel="noreferrer"
                            className="underline hover:opacity-80"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {loc.address}
                          </a>
                        </div>
                      )}
                      {loc.phone && (
                        <div className="mt-0.5 text-navy/75">
                          ☎ <a href={`tel:${String(loc.phone).replace(/\s+/g, "")}`} className="underline text-teal" onClick={(e) => e.stopPropagation()}>{loc.phone}</a>
                        </div>
                      )}
                      {active && loc.opening_hours && (
                        <div className="mt-1 text-navy/70 text-xs leading-relaxed">
                          {(["Mon","Tue","Wed","Thu","Fri","Sat","Sun"] as const).map((d) => {
                            const h = loc.opening_hours?.[d] as { closed?: boolean; open?: string; close?: string } | undefined;
                            if (!h) return null;
                            return (
                              <div key={d} className="flex justify-between gap-3">
                                <span>{d}</span>
                                <span className="tabular-nums">{h.closed ? "Closed" : h.open && h.close ? `${h.open}–${h.close}` : "—"}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          );

          return (
            <>
              {/* Desktop / tablet: inline expandable section */}
              {mapOpen && (
                <section
                  id="place-map"
                  aria-labelledby="place-location-heading"
                  className="mt-4 sm:mt-5 animate-reveal scroll-mt-24 hidden sm:block"
                >
                  <h2 id="place-location-heading" className="sr-only">Locations</h2>
                  {mapBody}
                </section>
              )}

              {/* Mobile: swipeable bottom sheet */}
              <MobileMapSheet
                open={mapOpen && isMobile}
                onClose={() => setMapOpen(false)}
                title={item.name}
                subtitle={`${locations.length} ${locations.length === 1 ? "location" : "locations"}`}
                initialSnap="half"
              >
                {mapBody}
              </MobileMapSheet>
            </>
          );
        })()}



        {(tab === "overview" || tab === "details") && (
          <>
            <section className="mt-8 max-w-3xl">
              <h2 className="font-heading text-navy text-lg sm:text-xl">
                {((tTaglineOverride || t(taglineKey)) ?? "A moment in {area}").replace("{area}", tArea ?? "")}
              </h2>
              <div className="mt-6 space-y-5 text-navy/85 leading-relaxed text-[15px]">
                <p>{tDescription}</p>
                {tab === "overview" && tOverviewBody && (
                  tOverviewBody.split(/\n\n+/).map((para: string, i: number) => (
                    <p key={i} className="whitespace-pre-wrap">{para}</p>
                  ))
                )}
                {tab === "overview" && isPreview && !item.overviewBody && (
                  <MissingFieldWarning field="Overview tab (overview_body)" />
                )}
                {tab === "details" && tDetailsBody && (
                  tDetailsBody.split(/\n\n+/).map((para: string, i: number) => (
                    <p key={i} className="whitespace-pre-wrap">{para}</p>
                  ))
                )}
                {tab === "details" && isPreview && !item.detailsBody && (
                  <MissingFieldWarning field="Details tab (details_body)" />
                )}
              </div>


              {tab === "details" && (
                <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {item.category && <Info label="category" value={tCategory} />}
                  {(item as any).region && (
                    <Info
                      label="region"
                    value={({ lapland: "Lapland", lakeland: "Lakeland", archipelago: "Archipelago", coast: "Coast of Finland" } as Record<string, string>)[(item as any).region] ?? (item as any).region}
                    />
                  )}
                  {item.area && <Info label="area" value={tArea} />}
                  {item.address && <Info label="address" value={item.address} />}
                  {item.price && <Info label="price" value={tPrice} />}
                  {(item as any).phone && <Info label="phone" value={<a href={`tel:${String((item as any).phone).replace(/\s+/g, "")}`} className="underline text-teal">{(item as any).phone}</a>} />}
                  {(item as any).email && <Info label="email" value={<a href={`mailto:${(item as any).email}`} className="underline text-teal break-all">{(item as any).email}</a>} />}
                  {item.link && <Info label="website" value={<a href={partnerHref(item.id, "website")} target="_blank" rel="noreferrer" className="underline text-teal break-all">{item.link}</a>} />}
                  {(item as any).instagram && <Info label="instagram" value={<a href={(item as any).instagram} target="_blank" rel="noreferrer" className="underline text-teal break-all">{String((item as any).instagram).replace(/^https?:\/\/(www\.)?instagram\.com\//i, "@")}</a>} />}
                  {(item as any).facebook && <Info label="facebook" value={<a href={(item as any).facebook} target="_blank" rel="noreferrer" className="underline text-teal break-all">{String((item as any).facebook).replace(/^https?:\/\/(www\.)?facebook\.com\//i, "")}</a>} />}
                </dl>
              )}

              {tab === "details" && locations.length > 0 && (
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[11px] font-mono uppercase tracking-widest text-navy/50">
                      All Locations ({locations.length})
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {(locations as any[]).map((loc, idx) => (
                      <li key={loc.id ?? idx} className="rounded-xl border border-navy/15 bg-white p-4">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center size-6 rounded-full bg-navy text-white text-[11px] font-bold">
                              {idx + 1}
                            </span>
                            <div className="font-heading text-navy">
                              {loc.label || loc.city_slug || item.name}
                            </div>
                            {loc.is_primary && (
                              <span className="text-[10px] font-bold uppercase tracking-widest text-teal bg-teal/10 px-2 py-0.5 rounded-full">
                                Primary
                              </span>
                            )}
                          </div>
                          {loc.lat != null && loc.lng != null ? (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedLocId(loc.id);
                                setMapOpen(true);
                                if (!isMobile) {
                                  requestAnimationFrame(() => {
                                    document.getElementById("place-map")?.scrollIntoView({ behavior: "smooth", block: "start" });
                                  });
                                }
                              }}
                              className="text-[11px] font-bold uppercase tracking-widest text-teal hover:underline shrink-0"
                            >
                              Show on map
                            </button>
                          ) : (
                            <span className="text-[10px] font-bold uppercase tracking-widest text-navy/40 bg-navy/5 px-2 py-0.5 rounded-full shrink-0">
                              No pin
                            </span>
                          )}
                        </div>
                        <ul className="space-y-1.5 text-sm text-navy/85">
                          {loc.address && (
                            <li className="flex items-start gap-2">
                              <MapPin className="size-4 mt-0.5 text-navy/60 shrink-0" />
                              <span>{loc.address}{loc.postal_code ? `, ${loc.postal_code}` : ""}</span>
                            </li>
                          )}
                          {loc.phone && (
                            <li className="flex items-start gap-2">
                              <span className="text-navy/60 shrink-0 w-4 text-center">☎</span>
                              <a href={`tel:${String(loc.phone).replace(/\s+/g, "")}`} className="underline text-teal">{loc.phone}</a>
                            </li>
                          )}
                          {loc.email && (
                            <li className="flex items-start gap-2">
                              <span className="text-navy/60 shrink-0 w-4 text-center">✉</span>
                              <a href={`mailto:${loc.email}`} className="underline text-teal break-all">{loc.email}</a>
                            </li>
                          )}
                        </ul>
                        {loc.opening_hours && (
                          <div className="mt-3 pt-3 border-t border-navy/10">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-navy/50 mb-1.5">Opening hours</div>
                            <table className="w-full text-xs">
                              <tbody>
                                {(["Mon","Tue","Wed","Thu","Fri","Sat","Sun"] as const).map((d) => {
                                  const h = loc.opening_hours?.[d] as { closed?: boolean; open?: string; close?: string } | undefined;
                                  if (!h) return null;
                                  return (
                                    <tr key={d} className="border-b last:border-0 border-navy/5">
                                      <td className="py-1 text-navy/70">{d}</td>
                                      <td className="py-1 text-right text-navy tabular-nums">
                                        {h.closed ? "Closed" : h.open && h.close ? `${h.open} – ${h.close}` : "—"}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}


            </section>

            {tab === "overview" && (
              <section className="mt-10 sm:mt-12 overflow-hidden rounded-2xl border border-navy/10 bg-white grid md:grid-cols-[280px_1fr]">
                <div className="relative aspect-[16/10] md:aspect-auto md:min-h-[220px] overflow-hidden bg-slate-100">
                  <SmartImage src={item.image} alt="" className="size-full object-cover" />
                  {tPrice ? (
                    <span className="absolute top-2.5 right-2.5 bg-navy text-white text-[11px] font-semibold px-2.5 py-1 rounded-full">
                      {tPrice}
                    </span>
                  ) : null}
                  {tArea ? (
                    <span className="absolute bottom-2.5 left-2.5 inline-flex items-center gap-1 rounded-full bg-black/45 px-2 py-1 text-[11px] text-white backdrop-blur-sm">
                      <MapPin className="size-3" /> {tArea}
                    </span>
                  ) : null}
                </div>
                <div className="p-4 sm:p-6 flex flex-col justify-center">
                  <h3 className="font-heading text-navy text-base sm:text-lg">
                    {tPromoTitleOverride || t(promoKey)}
                  </h3>
                  <p className="mt-2 text-navy/75 text-[13px] sm:text-sm leading-relaxed">
                    {tDescription} {tPromoBodyOverride || t("place.promo.body")}
                  </p>
                  {item.link && (
                    <a
                      href={partnerHref(item.id, "website")}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-4 inline-flex w-fit max-w-full items-center justify-center bg-navy hover:bg-royal text-white px-5 py-2.5 rounded-full text-xs font-semibold"
                    >
                      <span className="truncate">{t("place.unlock.website")}</span>
                    </a>
                  )}
                </div>
              </section>
            )}
          </>
        )}

        {tab === "tickets" && (
          <section className="mt-8 max-w-3xl">
            <h2 className="font-heading text-navy text-lg sm:text-xl">{ticketsPricingLabel}</h2>
            <div className="mt-5 rounded-2xl border border-navy/15 bg-white p-5 space-y-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-navy/70 text-sm">{priceLabel}</span>
                <span className="font-heading text-navy text-xl">{tPrice || seeWebsiteLabel}</span>
              </div>
              {item.ticketsInfo && (
                <div className="text-sm text-navy/85 leading-relaxed whitespace-pre-wrap pt-2 border-t border-navy/10">
                  {tTicketsInfo}
                </div>
              )}
              {isPreview && !item.ticketsInfo && (
                <MissingFieldWarning field="Tickets tab (tickets_info)" />
              )}

              {(item.buyTicketUrl ?? item.reserveLink ?? item.link) ? (
                <a
                  href={partnerHref(item.id, item.section === "happening" ? "tickets" : "reserve")}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 w-full inline-flex justify-center items-center gap-2 bg-navy hover:bg-royal text-white px-5 py-3 rounded-full text-xs font-bold uppercase tracking-widest"
                >
                  {item.section === "happening" ? buyTicketsLabel : bookReserveLabel}
                </a>
              ) : (
                !item.ticketsInfo && <p className="text-navy/60 text-sm">{noBookingLinkLabel}</p>
              )}
            </div>
          </section>
        )}

        {tab === "schedule" && (
          <section className="mt-8 max-w-3xl">
            <h2 className="font-heading text-navy text-lg sm:text-xl">{scheduleLabel}</h2>
            <div className="mt-5 rounded-2xl border border-navy/15 bg-white p-5 space-y-3 text-sm text-navy/85">
              {item.date && <Info label="when" value={item.date} testId="event-date-label" />}
              {item.startsAt && <Info label="starts" value={new Date(item.startsAt).toLocaleString()} />}
              {item.endsAt && <Info label="ends" value={new Date(item.endsAt).toLocaleString()} />}
              {(item as any).openingHours && (
                <div className="pt-2 border-t border-navy/10">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-navy/50 mb-2">opening hours</div>
                  <table className="w-full text-sm">
                    <tbody>
                      {(["Mon","Tue","Wed","Thu","Fri","Sat","Sun"] as const).map((d) => {
                        const h = (item as any).openingHours?.[d] as { closed?: boolean; open?: string; close?: string } | undefined;
                        if (!h) return null;
                        return (
                          <tr key={d} className="border-b last:border-0 border-navy/5">
                            <td className="py-1.5 text-navy/70">{d}</td>
                            <td className="py-1.5 text-right text-navy tabular-nums">
                              {h.closed ? "Closed" : h.open && h.close ? `${h.open} – ${h.close}` : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {item.scheduleInfo && !(item as any).openingHours && (
                <div className="text-sm text-navy/85 leading-relaxed whitespace-pre-wrap pt-2 border-t border-navy/10">
                  {tScheduleInfo}
                </div>
              )}

              {(item.address || (item as any).phone || (item as any).email || item.link || (item as any).instagram || (item as any).facebook) && (
                <div className="pt-4 border-t border-navy/10">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-navy/50 mb-3">Contact & Location</div>
                  <ul className="space-y-2 text-sm text-navy/85">
                    {item.address && (
                      <li className="flex items-start gap-2">
                        <MapPin className="size-4 mt-0.5 text-navy/60 shrink-0" />
                        <span>{item.address}</span>
                      </li>
                    )}
                    {(item as any).phone && (
                      <li className="flex items-start gap-2">
                        <span className="text-navy/60 shrink-0 w-4 text-center">☎</span>
                        <a href={`tel:${String((item as any).phone).replace(/\s+/g, "")}`} className="underline text-teal">{(item as any).phone}</a>
                      </li>
                    )}
                    {(item as any).email && (
                      <li className="flex items-start gap-2">
                        <span className="text-navy/60 shrink-0 w-4 text-center">✉</span>
                        <a href={`mailto:${(item as any).email}`} className="underline text-teal break-all">{(item as any).email}</a>
                      </li>
                    )}
                    {item.link && (
                      <li className="flex items-start gap-2">
                        <span className="text-navy/60 shrink-0 w-4 text-center">🌐</span>
                        <a href={partnerHref(item.id, "website")} target="_blank" rel="noreferrer" className="underline text-teal break-all">{item.link}</a>
                      </li>
                    )}
                    {(item as any).instagram && (
                      <li className="flex items-start gap-2">
                        <span className="text-navy/60 shrink-0 w-4 text-center">◉</span>
                        <a href={(item as any).instagram} target="_blank" rel="noreferrer" className="underline text-teal break-all">
                          Instagram: {String((item as any).instagram).replace(/^https?:\/\/(www\.)?instagram\.com\//i, "@")}
                        </a>
                      </li>
                    )}
                    {(item as any).facebook && (
                      <li className="flex items-start gap-2">
                        <span className="text-navy/60 shrink-0 w-4 text-center">f</span>
                        <a href={(item as any).facebook} target="_blank" rel="noreferrer" className="underline text-teal break-all">
                          Facebook: {String((item as any).facebook).replace(/^https?:\/\/(www\.)?facebook\.com\//i, "").slice(0, 60)}
                        </a>
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {isPreview && !item.scheduleInfo && !(item as any).openingHours && (
                <MissingFieldWarning field="Schedule tab (schedule_info / opening_hours)" />
              )}
              {!item.date && !item.startsAt && !item.endsAt && !item.scheduleInfo && !(item as any).openingHours && !item.address && !(item as any).phone && !(item as any).email && (
                <p className="text-navy/60">{noScheduleInfoLabel}</p>
              )}


            </div>
          </section>
        )}

        {tab === "reviews" && (
          <section id="place-reviews" className="mt-8 max-w-3xl scroll-mt-24">
            <h2 className="font-heading text-navy text-lg sm:text-xl mb-5">{reviewsHeadingLabel}</h2>
            <PlaceReviews placeSlug={item.id} autoFocusComposer />
          </section>
        )}


        {/* Menu — always in the server-rendered HTML (not tab-gated) so search
            and AI crawlers can read it on first load. */}
        <PlaceMenuSection
          menu={(item as any).menu}
          menus={(item as any).menuDocs ?? []}
          dishes={(item as any).menuDishes ?? []}
          placeName={item.name}
          lang={lang === "fi" ? "fi" : "en"}
        />

        <section className="mt-12 sm:mt-14">
          <h2 className="font-heading text-navy text-xl sm:text-2xl">{t("place.related")}</h2>
          <div className="mt-4 sm:mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {related.map((r: Item) => <RelatedCard key={r.id} item={r} />)}
          </div>
        </section>

        <section className="mt-12 sm:mt-16 text-center">
          <h2 className="font-heading text-navy text-xl sm:text-3xl">
            {tCtaTitleOverride || t("listing.cta.title")}
          </h2>
          <p className="mt-3 text-navy/70 max-w-2xl mx-auto text-[13px] sm:text-sm">
            {tCtaBodyOverride || t("listing.cta.body")}
          </p>
        </section>
      </div>

      <section className="relative h-[260px] sm:h-[320px] overflow-hidden">
        <img src={heroBanner} alt="" className="absolute inset-0 size-full object-cover" />
        <div className="absolute inset-0 bg-navy/30" />
        <div className="relative h-full grid place-items-center px-6">
          <Link
            to={item.section === "eat" ? "/eat" : item.section === "do" ? "/do" : "/happening"}
            className="bg-white text-navy hover:bg-teal hover:text-white transition-colors px-8 py-4 rounded-full text-sm font-medium inline-flex items-center gap-2 shadow-2xl"
          >
            <Search className="size-4" />
            {tSearchOverride || t(searchKey)}
          </Link>
        </div>
      </section>
    </div>
  );
}


function Info({ label, value, testId }: { label: string; value: ReactNode; testId?: string }) {
  const translatedLabel = useAutoTranslate(label);
  return (
    <div className="rounded-xl border border-navy/10 bg-slate-50/70 px-3 py-2.5 flex flex-col gap-0.5">
      <span className="text-navy/50 text-[11px] font-medium uppercase tracking-wide">{translatedLabel}</span>
      <span className="text-navy text-sm leading-snug" data-testid={testId}>{value}</span>
    </div>
  );
}

function RelatedCard({ item }: { item: Item }) {
  const { t } = useLanguage();
  const stat = useReviewStat(item.id);
  const hasReviews = stat.count >= 5;
  const rating = stat.avg;
  const name = useProperName(item.name);
  const area = useAutoTranslate(item.area);
  const noReviewsLabel = useAutoTranslate("no reviews yet");
  const starsLabel = useAutoTranslate("stars");
  return (
    <Link
      to="/place/$id"
      params={{ id: item.id }}
      className="group block rounded-2xl overflow-hidden border border-navy/10 bg-white"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
        <SmartImage src={item.image} alt={item.category ? `${item.category} — ${name}` : `Photo of ${name}`} className="size-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-navy/10 to-transparent" />
        {hasReviews && (
          <div className="absolute top-2 left-2 flex gap-0.5" aria-label={`${rating.toFixed(1)} ${starsLabel}`}>
            {Array.from({ length: 5 }).map((_, i) => (
              <StarIcon
                key={i}
                className={`size-2.5 sm:size-3 ${i < Math.round(rating) ? "text-[var(--star)]" : "text-[var(--star)]/25"}`}
              />
            ))}
          </div>
        )}
        <div className="absolute bottom-2 left-2 right-2 text-white">
          <p className="font-heading text-xs sm:text-sm leading-tight">{name}</p>
          <p className="text-[10px] sm:text-[11px] opacity-90">
            {area}
            {hasReviews ? ` · ${rating.toFixed(1)}★ (${stat.count} ${t("card.reviews")})` : ""}
          </p>
        </div>
      </div>
    </Link>
  );
}

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Days keyed the same way opening_hours is stored on place_locations.
const DAY_KEYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/**
 * True if `hours` describes a schedule that currently covers "now" in the
 * viewer's local time. Missing/empty schedules are treated as "unknown" and
 * excluded from the "open now" filter (returns false).
 */
function isOpenNow(hours: unknown): boolean {
  if (!hours || typeof hours !== "object") return false;
  const now = new Date();
  const day = DAY_KEYS[now.getDay()];
  const today = (hours as Record<string, { open?: string | null; close?: string | null; closed?: boolean } | undefined>)[day];
  if (!today || today.closed) return false;
  const open = parseHM(today.open);
  const close = parseHM(today.close);
  if (open == null || close == null) return false;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  // Overnight window (e.g. 18:00–02:00): open if >= open OR < close.
  if (close <= open) return nowMin >= open || nowMin < close;
  return nowMin >= open && nowMin < close;
}

function parseHM(v: string | null | undefined): number | null {
  if (!v) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(v.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(mm)) return null;
  return h * 60 + mm;
}
